import {
  AnalysisResult,
  MatchType,
  AnalysisStage,
  CalibrationStatus,
  RecordingMetadata
} from "../types";

import { getWorkerInstance } from "./workerFactory";
import { AnalysisResultFactory } from "./analysis/AnalysisResultFactory";
import { normalizeDspPayload } from "./dspAdapter";
import { validateDspPayload } from "./dspSchema";
import { computeScore } from "./scoreService";
import { validateGridFromChroma } from "./harmony/chordDetector";
import { setLastAnalysisRawPayload, setLastResult } from "./analysisCache";
import { decodeAudioFile } from "./audioDecoder";
import { APP_CONFIG } from "./config";
import { normalizeHardwareLagMs } from "./latencyUnits";
import { getRuntimeContext } from "./runtimeContext";

export interface AnalysisPipelineParams {
  studentFile: File;
  referenceFile: File | null;
  targetBpm: number;
  matchType: MatchType;
  level: { id: number; name: string };
  grid: string;
  capture: {
    studentSource: string;
    timeSignature: string;
    soundProfile: string;
  };
  hardwareLagMs: number;
  startOffsetMs: number;
  qualityWarning?: string;
  onStageChange?: (stage: AnalysisStage) => void;
  isAuthorizedForScoring?: boolean;
  calibrationStatus?: CalibrationStatus;
  metadata?: RecordingMetadata;
}

let _reqSeq = 0;

/**
 * Worker bridge — request/response strict.
 */
export async function runWorkerTask<T>(type: string, payload: any): Promise<T> {
  const worker = getWorkerInstance();
  const requestId = ++_reqSeq;

  const transferables: Transferable[] = [];
  if (payload.stuData instanceof ArrayBuffer) transferables.push(payload.stuData);
  else if (payload.stuData?.buffer instanceof ArrayBuffer) transferables.push(payload.stuData.buffer);

  if (payload.refData instanceof ArrayBuffer) transferables.push(payload.refData);
  else if (payload.refData?.buffer instanceof ArrayBuffer) transferables.push(payload.refData.buffer);

  return new Promise((resolve, reject) => {
    const handler = (e: MessageEvent) => {
      if (e.data.requestId === requestId) {
        worker.removeEventListener("message", handler);
        if (e.data.error) reject(new Error(e.data.error.message));
        else resolve(e.data.payload);
      }
    };

    worker.addEventListener("message", handler);
    worker.postMessage({ type, payload, requestId }, transferables);
  });
}

/**
 * ✅ Runtime duration truth from PCM length + sampleRate.
 * - No envelope-based duration.
 * - Deterministic.
 */
function pcmDurationSec(pcm: Float32Array, sampleRate: number): number {
  const sr = Number(sampleRate || 0);
  if (!pcm || !Number.isFinite(sr) || sr <= 0) return 1;
  const d = pcm.length / sr;
  return Number.isFinite(d) && d > 0 ? d : 1;
}

/**
 * MAIN ANALYSIS PIPELINE
 * ---------------------
 * ⚠️ Aucun appel implicite au temps ou à l'aléa.
 */
export const analyzeAudio = async (
  params: AnalysisPipelineParams
): Promise<AnalysisResult> => {
  const runtime = getRuntimeContext();
  const sessionId = `gsc_session_${runtime.uuid()}`;

  const {
    studentFile,
    referenceFile,
    onStageChange,
    isAuthorizedForScoring = true,
    calibrationStatus = "VALID",
    metadata
  } = params;

  const isPremium = localStorage.getItem("gsc_is_premium") === "true";
  const analysisCount = parseInt(localStorage.getItem("gsc_analysis_count") || "0");

  onStageChange?.("DECODING");
  const stuPcm = await decodeAudioFile(studentFile);

  let refPcm: Float32Array | null = null;
  if (referenceFile) {
    refPcm = await decodeAudioFile(referenceFile);
  }

  // ✅ duration truth computed from decoded PCM (no envelope, no UI)
  const durationSecTruth = pcmDurationSec(stuPcm, APP_CONFIG.TARGET_SAMPLE_RATE);

  const finalHardwareLag = normalizeHardwareLagMs(params.hardwareLagMs);

  onStageChange?.("DSP_WORKER");
  const dspRes = await runWorkerTask<any>("ANALYZE_FULL", {
    stuData: stuPcm.buffer,
    refData: refPcm ? refPcm.buffer : null,
    sampleRate: APP_CONFIG.TARGET_SAMPLE_RATE,
    targetBpm: params.targetBpm,
    hardwareLagMs: finalHardwareLag
  });

  onStageChange?.("SCORING");

  // 1) Schema validation
  const validated = validateDspPayload(dspRes).sanitized;

  // ✅ VERSION A: inject durationSec into RAW DSP payload BEFORE adapter
  // This makes normalizeDspPayload pick the truth and prevents envelope-derived duration.
  (validated as any).durationSec = durationSecTruth;

  // 2) Adapter normalization
  const normalized = normalizeDspPayload(validated);

  // ✅ runtime values
  normalized.hardwareLagMs = finalHardwareLag;

  // ⚠️ startOffsetMs est géré par metadata/slate dans d'autres versions.
  // Ici on reste strict: on ne reconstruit pas.
  normalized.startOffsetMs = 0;

  // ✅ enforce normalized.duration truth (in case adapter fallback existed)
  // Keep it deterministic and stable.
  normalized.duration = durationSecTruth;

  const harmonyRes = await validateGridFromChroma(
    normalized.spectral.stuChroma,
    params.grid?.split(" ") || [],
    {
      bpm: params.targetBpm,
      timeSignature: (params.capture.timeSignature as any) || "4/4",
      frameHopSec: 0.15,
      durationSec: normalized.duration,
      lagMs: finalHardwareLag,
      soundProfile: (params.capture.soundProfile as any) || "CLEAN"
    }
  );

  let report = computeScore(
    normalized,
    params.level.id,
    harmonyRes.harmonyFromGrid || undefined,
    { isPremium, analysisCount },
    calibrationStatus
  );

  if (!isAuthorizedForScoring) {
    report = { ...report, score: null, status: "INVALID" };
  }

  // ==========================
  // 🔒 FINGERPRINT RUNTIME ONLY
  // ==========================
  // On ne "fabrique" rien. On lit uniquement ce qui existe dans le payload normalisé.
  const sp: any = (normalized as any)?.spectral || {};

  const pickArr = (...candidates: any[]): number[] => {
    for (const c of candidates) {
      if (Array.isArray(c) && c.length > 0) return c;
    }
    return [];
  };

  // Noms possibles selon versions DSP / adapter
  const stuFingerprint: number[] = pickArr(
    sp.stuFingerprint,
    sp.stuMfcc,
    sp.stuMFCC,
    sp.mfccStu,
    sp.mfccDataStu
  );

  const refFingerprint: number[] = pickArr(
    sp.refFingerprint,
    sp.refMfcc,
    sp.refMFCC,
    sp.mfccRef,
    sp.mfccDataRef
  );

  // ==================
  // RESULT BUILD
  // ==================
  const res = await AnalysisResultFactory.create({
    sessionId,
    dsp: normalized,
    report,
    harmonyRes,
    gridTokens: params.grid?.split(" ") || [],
    targetBpm: params.targetBpm,
    activeLevelId: params.level.id,
    stuHash: "PROCESSED",
    studentSource: params.capture.studentSource || "Live",
    stuFile: studentFile,
    refFile: referenceFile,
    fallbackActive: false,
    matchType: params.matchType,
    integrityStatus: isAuthorizedForScoring ? "PASS" : "FAIL",
    calibrationStatus
  });

  // ✅ Guarantee session.duration truth for all UI consumers (charts/video/vision)
  // Only set if field exists; does not break if factory already set it.
  try {
    (res as any).session = (res as any).session || {};
    (res as any).session.duration = durationSecTruth;
  } catch {}

  // ==========================
  // DEBUG — RUNTIME ONLY
  // ==========================
  // debug peut être undefined selon contrat => on initialise sans casser
  const prevDbg = (res.session.metrics.debug || {}) as any;

  res.session.metrics.debug = {
    ...prevDbg,
    hardwareLagMs: finalHardwareLag,
    durationSecTruth,
    // On expose ces vecteurs pour l’anti-clone (checkSpectralSimilarity)
    // S’ils sont vides => la décision sera "UNVERIFIABLE" côté orchestrator (normal).
    stuFingerprint,
    refFingerprint,
    // Trace utile
    hasStuFingerprint: Array.isArray(stuFingerprint) && stuFingerprint.length > 0,
    hasRefFingerprint: Array.isArray(refFingerprint) && refFingerprint.length > 0
  };

  setLastAnalysisRawPayload(
    sessionId,
    { ...normalized, calibration_status: calibrationStatus },
    harmonyRes.harmonyFromGrid
  );

  setLastResult(res);

  (globalThis as any).__GSC_LAST_RESULT__ = res;
  (globalThis as any).__GSC_LAST_SESSION_ID__ = sessionId;

  return res;
};
